import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useCloseOnEscape } from '../../hooks/useCloseOnEscape';
import { useFilters } from './hooks/useFilters';
import { useDatePicker } from './hooks/useDatePicker';
import { usePagination } from './hooks/usePagination';
import { useMap } from './hooks/useMap';
import { useSearchResults } from './hooks/useSearchResults';
import { VENUES_PER_PAGE, MOBILE_BREAKPOINT } from './constants';
import { ListViewIcon, MapViewIcon } from '../icons';
import FilterBar from './components/FilterBar';
import VenueCard from './components/VenueCard';
import Pagination from './components/Pagination';
import MapPanel from './components/MapPanel';
import NoResults from './components/NoResults';
import './SearchResults.css';
import { isSearchPriceFilterEnabled } from '../../utils/venuePricing';

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const aiVenuesFromState = location.state?.aiVenues;
  const intakeSource = searchParams.get('source') === 'intake' || Boolean(location.state?.fromIntake);

  // Initialize local search data from URL parameters
  const [localSearchData, setLocalSearchData] = useState({
    location: '',
    date: '',
    eventType: '',
    venueBriefRequestId: ''
  });
  
  // View mode state
  const [viewMode, setViewMode] = useState('list');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);

  // Custom hooks
  const {
    filters,
    showFilters,
    showSortDropdown,
    sortBy,
    setShowFilters,
    setShowSortDropdown,
    setSortBy,
    handleFilterSelect,
    toggleFilterDropdown,
    closeAllDropdowns
  } = useFilters();

  useEffect(() => {
    if (
      !isSearchPriceFilterEnabled() &&
      (sortBy === 'price-low' || sortBy === 'price-high')
    ) {
      setSortBy('popularity');
    }
  }, [sortBy, setSortBy]);

  const datePicker = useDatePicker();
  const {
    showDatePicker: datePickerVisible,
    setShowDatePicker: setDatePickerVisible,
    currentMonth,
    currentYear,
    handleDateSelect: handleDateSelectInternal,
    handleMonthChange
  } = datePicker;
  
  // Merge date picker state with filters
  const showDatePicker = datePickerVisible;

  const anyFilterOpen = showDatePicker || showSortDropdown || Object.values(showFilters).some(Boolean);
  useCloseOnEscape(anyFilterOpen, closeAllDropdowns);

  // Initialize local search data from URL parameters
  useEffect(() => {
    setLocalSearchData({
      location: searchParams.get('location') || '',
      date: searchParams.get('date') || '',
      eventType: searchParams.get('eventType') || '',
      venueBriefRequestId:
        searchParams.get('venueBriefRequestId') ||
        location.state?.venueBriefRequestId ||
        ''
    });
  }, [searchParams, location.state]);

  // Fetch search results (or use AI venues from Landing2 if present)
  const { results, loading } = useSearchResults(localSearchData, filters, aiVenuesFromState);

  // Pagination
  const {
    currentPage,
    totalPages,
    startIndex,
    endIndex,
    currentItems: currentVenues,
    handlePageChange,
    handlePreviousPage,
    handleNextPage
  } = usePagination(results, VENUES_PER_PAGE);

  // Map
  const {
    mapElementRef,
    mapInstance,
    mapLoading,
    mapError,
    initializeOptimizedMap
  } = useMap(results, localSearchData, navigate, isMobile, viewMode);

  // Track window size changes
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.filter-dropdown-container')) {
        closeAllDropdowns();
        setDatePickerVisible(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [closeAllDropdowns, setDatePickerVisible]);

  // Handle date select
  const handleDateSelect = (dateObj) => {
    handleDateSelectInternal(dateObj, (formattedDate) => {
      setLocalSearchData(prev => ({ ...prev, date: formattedDate }));
    });
  };

  // Handle filter toggle with date picker coordination
  const handleToggleDatePicker = () => {
    const newState = !showDatePicker;
    setDatePickerVisible(newState);
    if (newState) {
      // Close all other dropdowns when opening date picker
      closeAllDropdowns();
    }
  };

  // Wrapper for filter toggle that also closes date picker
  const handleToggleFilter = (filterType) => {
    setDatePickerVisible(false);
    toggleFilterDropdown(filterType);
  };

  // Handle sort select
  const handleSortSelect = (value) => {
    setSortBy(value);
    setShowSortDropdown(false);
  };

  // Handle toggle sort dropdown
  const handleToggleSort = () => {
    const newState = !showSortDropdown;
    setShowSortDropdown(newState);
    setDatePickerVisible(false);
    setShowFilters(prev => ({
      price: false,
      vibe: false,
      eventType: false,
      attendees: false,
      moreFilters: false,
    }));
  };

  if (loading) {
    return (
      <div className="search-results-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Searching venues...</p>
          <p className="loading-subtitle">Finding the perfect space for your event</p>
        </div>
      </div>
    );
  }

  return (
    <div className="search-results-page">
      {intakeSource && (
        <div className="content-width container-padding mx-auto pt-4">
          <div className="rounded-xl border border-[#d4eadf] bg-[#f3faf6] px-4 py-3 text-[#0f5132]">
            <p className="text-sm font-semibold">Your request is in with our concierge team.</p>
            <p className="text-sm mt-1">You can browse and book any available venue now while we also review matches for you.</p>
          </div>
        </div>
      )}
      
      <FilterBar
        filters={filters}
        showFilters={showFilters}
        showDatePicker={showDatePicker}
        showSortDropdown={showSortDropdown}
        sortBy={sortBy}
        currentMonth={currentMonth}
        currentYear={currentYear}
        selectedDate={localSearchData.date}
        onToggleDatePicker={handleToggleDatePicker}
        onToggleFilter={handleToggleFilter}
        onToggleSort={handleToggleSort}
        onFilterSelect={handleFilterSelect}
        onSortSelect={handleSortSelect}
        onDateSelect={handleDateSelect}
        onMonthChange={handleMonthChange}
      />
     

     <div className="results-header-container">
      <div className="results-header">
            <span className="results-count">
              {results.length} venue{results.length !== 1 ? 's' : ''} in the selected area
            </span>
            
            {/* View Switcher - Only show on mobile and tablet */}
            {isMobile && (
        <div className="mobile-view-switcher">
          <button 
            className={`view-switch-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
            title="List view"
          >
            <ListViewIcon />
          </button>
          <button 
            className={`view-switch-btn ${viewMode === 'map' ? 'active' : ''}`}
            onClick={() => setViewMode('map')}
            title="Map view"
          >
            <MapViewIcon />
          </button>
        </div>
      )}
          </div>
          </div>
      <div className={`main-content-search-results ${viewMode === 'map' && isMobile ? 'map-view' : ''}`}>
        <div className="results-panel"> 
          {/* List View */}
          {viewMode === 'list' && (
            <div className="results-grid">
              {currentVenues.map((venue) => (
                <VenueCard
                  key={venue.id}
                  venue={venue}
                  searchParams={localSearchData}
                />
            ))}
            </div>
          )}

          {/* No results message */}
          {results.length === 0 && !loading && (
            <NoResults location={localSearchData.location} />
          )}
          
          {/* Pagination */}
          {(viewMode === 'list' || !isMobile) && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              startIndex={startIndex}
              endIndex={endIndex}
              totalItems={results.length}
              onPageChange={handlePageChange}
              onPreviousPage={handlePreviousPage}
              onNextPage={handleNextPage}
            />
          )}
        </div>

        {/* Map Right Panel */}
        {(viewMode === 'map' || !isMobile) && (
          <MapPanel
            mapElementRef={mapElementRef}
            mapInstance={mapInstance}
            mapLoading={mapLoading}
            mapError={mapError}
            onRetry={() => {
              initializeOptimizedMap(true);
            }}
            onRefresh={() => {
              window.location.reload();
            }}
          />
        )}
      </div>
    </div>
  );
};

export default SearchResults;

